import React, {createContext, useCallback, useContext, useEffect} from 'react'

import {useQueryClient} from '@tanstack/react-query'
import {SystemRole} from '../../Constants'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import {useAuth} from '../authentication/AuthProvider'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {
    setStorageRoleConfiguration,
    useUserRolePermissions,
} from './query-keys/RoleKeysManager'

const RoleContext = createContext({})

export const RoleProvider = React.memo(({children}) => {
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()

    const queryClient = useQueryClient()

    const {data} = useUserRolePermissions(
        user?.id,
        currentHospital?.id,
        userRole,
    )

    useEffect(() => {
        setStorageRoleConfiguration(data)
    }, [data])

    //#region Check Role Permission
    // This functions returns a list of allowed permission keys based on the role category
    const getPermissionsByRoleCategory = useCallback(
        roleCategory => {
            if (
                !data ||
                !isArrayAndNotEmpty(data?.permissions) ||
                !roleCategory
            ) {
                return []
            }

            const permissions = data?.permissions

            const permissionByRoleCategory = permissions?.find(
                p => p.roleCategory === roleCategory,
            )

            if (userRole === SystemRole.USER_WITH_ROLE) {
                if (
                    !permissionByRoleCategory ||
                    Object.keys(permissionByRoleCategory).length === 0
                ) {
                    return []
                }

                return Object.keys(permissionByRoleCategory).filter(
                    key =>
                        key !== 'roleCategory' && permissionByRoleCategory[key],
                )
            }
        },
        [data, userRole],
    )

    const isQualifiedForTakingCareOfPatients = () => {
        if (userRole === SystemRole.ADMIN) {
            return true
        }

        const permissions = data?.permissions || []

        const canTakeCarePatients = permissions?.find(
            permission => permission?.qualified,
        )

        return !!canTakeCarePatients
    }
    //#endregion

    // TODO - Implement Role Signal Management {EventListener}

    return (
        <RoleContext.Provider
            value={{
                myRoleName: data?.roleName,
                myRolePermissions: data,
                isQualifiedForTakingCareOfPatients:
                    isQualifiedForTakingCareOfPatients(),

                hcpManagementPermissions:
                    getPermissionsByRoleCategory('hcpManagement'),
                patientManagementPermissions:
                    getPermissionsByRoleCategory('patientManagement'),
                pathwayManagementPermissions:
                    getPermissionsByRoleCategory('pathwayManagement'),
                supportTeamManagementPermissions: getPermissionsByRoleCategory(
                    'supportTeamsManagement',
                ),
                generalPractitionerManagementPermissions:
                    getPermissionsByRoleCategory(
                        'generalPractitionerManagement',
                    ),
            }}>
            {children}
        </RoleContext.Provider>
    )
})

export const useRoleContext = () => useContext(RoleContext)
